import React, { useState } from 'react';
import { FiFileText, FiPlus } from 'react-icons/fi';
import Toggle from '../../../Toggle';
import AddQuestionModal from './AddQuestionModal';
import GripIcon from './GripIcon';
import { FormQuestion, TabProps } from './formSettingsData';

const move = <T,>(list: T[], from: number, to: number) => {
    const next = [...list];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
};

// Form settings › Questions on the form (spec 713:6230). Drag (or arrow keys on the handle) to reorder; the toggle hides a question.
// Full name, email, subject and message are always asked and always required.
const Questions: React.FC<TabProps> = ({ form, setForm }) => {
    const [dragging, setDragging] = useState<string | null>(null);
    const [adding, setAdding] = useState(false);

    const setQuestions = (update: (qs: FormQuestion[]) => FormQuestion[]) => setForm(f => ({ ...f, questions: update(f.questions) }));
    const patch = (id: string, change: Partial<FormQuestion>) => setQuestions(qs => qs.map(q => q.id === id ? { ...q, ...change } : q));

    // dragging over a row moves the dragged question into its place
    const dragOver = (e: React.DragEvent, overId: string) => {
        e.preventDefault();
        if (!dragging || dragging === overId) return;
        setQuestions(qs => move(qs, qs.findIndex(q => q.id === dragging), qs.findIndex(q => q.id === overId)));
    };

    const keyMove = (e: React.KeyboardEvent, index: number) => {
        let to = -1;
        if (e.key === 'ArrowUp') to = index - 1;
        else if (e.key === 'ArrowDown') to = index + 1;
        if (to < 0 || to >= form.questions.length) return;
        e.preventDefault();
        setQuestions(qs => move(qs, index, to));
    };

    return <>
        <section className="card">
            <div className="section-title">
                <span className="icon-tile"><FiFileText /></span>
                <div className="text">
                    <h2>Questions on the form</h2>
                    <div className="hint">Turn a question off to hide it from the website form</div>
                </div>
            </div>

            <div className="hub-fs-questions">
                {form.questions.map((q, i) => <div key={q.id} className={`hub-fs-question${q.shown ? '' : ' is-off'}${dragging === q.id ? ' is-dragging' : ''}`}
                    draggable onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', q.id); setDragging(q.id); }}
                    onDragOver={e => dragOver(e, q.id)} onDrop={e => e.preventDefault()} onDragEnd={() => setDragging(null)}>
                    <button type="button" className="hub-fs-grip" aria-label={`Move ${q.label} (arrow up / down)`} title="Drag to reorder" onKeyDown={e => keyMove(e, i)}><GripIcon /></button>
                    <div className="hub-fs-q-text">
                        <strong>{q.label}</strong>
                        <span>{q.detail ?? (q.required ? 'Required' : 'Optional')}</span>
                    </div>
                    {q.locked
                        ? <span className="hub-fs-req" title="Always required">Required</span>
                        : <button type="button" className={`hub-fs-req${q.required ? '' : ' is-optional'}`} aria-pressed={q.required}
                            title={q.required ? 'Make optional' : 'Make required'} onClick={() => patch(q.id, { required: !q.required })}>
                            {q.required ? 'Required' : 'Make required'}
                        </button>}
                    <Toggle label={`Show ${q.label} on the form`} checked={q.shown} locked={q.locked} onChange={shown => patch(q.id, { shown })} />
                </div>)}
            </div>

            <button type="button" className="btn-outline with-icon" onClick={() => setAdding(true)}><FiPlus /> Add a Question</button>
        </section>

        {adding && <AddQuestionModal onClose={() => setAdding(false)}
            onAdd={q => { setQuestions(qs => [...qs, q]); setAdding(false); }} />}
    </>;
};

export default Questions;
